import { Inertia } from '@inertiajs/inertia'
import { Component, Event, EventEmitter, getAssetPath, h, Listen, Prop, State, Watch } from '@stencil/core'
import Axios from 'axios'
import { InlineIconText } from 'src/ui/components'

const versionToLastStepMap = {
  1: 10,
  2: 7,
  3: 6,
} as const

@Component({
  tag: 'onboarding-tutorial',
  assetsDirs: ['assets'],
})
export class OnboardingTutorial {
  @Event() closed: EventEmitter
  @Event() completedTutorial: EventEmitter
  @Event() skippedTutorial: EventEmitter

  @Prop() tutorialVersion = 3
  @Prop() viewedTutorialStepUrl!: string
  @Prop() tutorialContext: 'page' | 'modal' = 'page'
  @Prop() completedTutorialUrl!: string
  @Prop() skippedTutorialUrl!: string
  @Prop() postTutorialUrl?: string

  @State() step = 1
  reachedEnd = false
  viewedSteps: Array<number> = []

  dialogEl: HTMLUiDialogElement | undefined

  @Watch('tutorialVersion')
  onVersionSwitch() {
    this.step = 1
  }

  componentWillLoad() {
    this.onVersionSwitch()
  }

  async componentDidRender() {
    await this.reportProgress()
  }

  get atBeginning() {
    return this.step === 1
  }

  get atEnd() {
    return this.step === this.lastStep
  }

  get lastStep(): number {
    return versionToLastStepMap[this.tutorialVersion] ?? 1
  }

  @Listen('keydown', { target: 'window' })
  async handleKeyboardNav(ev: KeyboardEvent) {
    if (ev.key === 'ArrowLeft') {
      this.previousStep()
    } else if (ev.key === 'ArrowRight') {
      await this.nextStep()
    }
  }

  get isModal(): boolean {
    return this.tutorialContext === 'modal'
  }

  previousStep() {
    if (this.atBeginning && this.isModal) {
      this.dialogEl.close()
    } else if (!this.atBeginning) {
      this.step -= 1
    }
  }

  async nextStep() {
    if (this.atEnd) {
      await this.endTutorial()
    } else {
      this.step += 1
      if (this.atEnd) {
        this.reachedEnd = true
      }
    }
  }

  async reportProgress() {
    if (this.step && !this.viewedSteps.includes(this.step)) {
      this.viewedSteps.push(this.step)
      await Axios.post(this.viewedTutorialStepUrl, { viewed_step: this.step, last_step: this.lastStep })
    }
  }

  async endTutorial() {
    if (this.reachedEnd) {
      await Axios.post(this.completedTutorialUrl)
      this.completedTutorial.emit()
    } else {
      await Axios.post(this.skippedTutorialUrl)
      this.skippedTutorial.emit()
    }

    if (!this.isModal) {
      Inertia.visit(this.postTutorialUrl)
    }
  }

  getImagePath(step: number): string {
    let imageType = 'jpg'

    if (this.tutorialVersion === 1) {
      imageType = 'svg'
    }

    return getAssetPath(`./assets/${this.tutorialVersion}/${step}.${imageType}`)
  }

  innerContent() {
    return (
      <div class='w-screen'>
        <div class='mx-auto' style={{ width: '70vw', minWidth: '640px' }}>
          <div class='relative'>
            <button
              class='absolute top-1/2 left-0 w-22 h-22 text-brand hover:text-black bg-black hover:bg-brand rounded-full border-2 border-brand hover:border-black transform -translate-x-1/2 -translate-y-1/2'
              onClick={() => this.previousStep()}
              disabled={this.atBeginning && !this.isModal}
              style={this.atBeginning && !this.isModal ? { display: 'none' } : {}}
            >
              <InlineIconText
                class='text-2xl font-extrabold uppercase'
                icon='chevron-left'
                iconClass='text-3xl -ml-3 -mr-1'
              >
                Prev
              </InlineIconText>
            </button>
            {Array.from(Array(this.lastStep).keys()).map((i) => (
              <img
                key={`tutorial-${i}`}
                hidden={this.step !== i + 1}
                class='object-contain object-center w-full h-full'
                src={this.getImagePath(i + 1)}
              />
            ))}
            <button
              class='absolute top-1/2 right-0 w-22 h-22 text-brand hover:text-black bg-black hover:bg-brand rounded-full border-2 border-brand hover:border-black transform translate-x-1/2 -translate-y-1/2'
              onClick={async () => await this.nextStep()}
            >
              <InlineIconText
                class='text-2xl font-extrabold uppercase'
                flip
                icon='chevron-right'
                iconClass='text-3xl -mr-3 -ml-1'
              >
                Next
              </InlineIconText>
            </button>
          </div>
        </div>
      </div>
    )
  }

  render() {
    if (this.tutorialContext === 'page') {
      return (
        <div class='flex flex-col justify-center items-center w-full h-screen text-base'>
          <ui-logo style={{ marginBottom: '5vh' }} />
          {this.innerContent()}
          <ui-button
            class='mx-auto'
            style={{ marginTop: '3vh' }}
            rounded='full'
            larger
            uppercase={false}
            hoverColor='brand-base'
            onClick={async () => {
              this.endTutorial()
            }}
          >
            Exit Tutorial
          </ui-button>
        </div>
      )
    }

    return (
      <ui-dialog
        ref={(el) => (this.dialogEl = el)}
        onClosed={() => this.endTutorial()}
        content={() => this.innerContent()}
      />
    )
  }
}
